import { Dropdown, LoadingSpinner, PaginationBar, UserAvatar, UserIcon, usePagination, useSearch } from '@cfs-fixings/shared'
import { CustomerResource, ViewUserResource, useGetCustomerUsersQuery } from '@/api/serverApi'
import { useDebouncedCallback } from 'use-debounce'
import FilterTeamMembersForm from '../forms/team-members/FilterTeamMembersForm'
import ResetUserPasswordModal from '../modals/ResetUserPasswordModal'
import { useState } from 'react'
import EditTeamMemberModal from '../modals/team-members/EditTeamMemberModal'
import DeleteTeamMemberModal from '../modals/team-members/DeleteTeamMemberModal'

const textSpec = {
  header: 'Members',
  table: {
    pendingInviteLabel: 'Pending invite...',
    emptyLabel: '-',
    actions: {
      resetPassword: 'Reset Password',
      edit: 'Edit User...',
      delete: 'Delete User',
    },
  },
}

type PropTypes = {
  customer: CustomerResource
}

const TeamMemberList = ({ customer }: PropTypes) => {
  const [selectedUser, setSelectedUser] = useState<ViewUserResource | undefined>(undefined)

  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const { currentPage, setPage, itemsPerPage, setItemsPerPage } = usePagination({})

  const { searchTerm, setSearchTerm } = useSearch()

  const setDebouncedSearch = useDebouncedCallback(setSearchTerm, 1000)

  const { data: response, isFetching } = useGetCustomerUsersQuery({
    customerId: customer.id,
    page: currentPage,
    perPage: itemsPerPage,
    search: searchTerm,
  })

  return (
    <>
      <div className="flex flex-col justify-between w-full h-full gap-4">
        <div className="flex flex-col gap-8">
          <div>
            <h2 className="text-xl leading-[21.6px] font-semibold pb-2.5">{textSpec.header}</h2>
            <FilterTeamMembersForm customer={customer} onSearchChange={setDebouncedSearch} />
          </div>
          <div className="overflow-x-scroll md:overflow-visible">
            {
              response && response.data.map((user) => (
                <div
                  key={`users-${user.id}`}
                  className="grid grid-cols-4 py-4 gap-2 border-b border-gray-400 last-of-type:border-b-0"
                >
                  <div className="flex items-center  h-[30px]">
                    {
                      user.invite_accepted_at
                        ? <div className="flex flex-row gap-2.5 items-center">
                          <UserAvatar
                            user={user}
                            size="small"
                          />
                          {user.first_name}
                        </div>
                        : <p className="text-sm leading-[14px] font-medium text-gray-600">
                          {textSpec.table.pendingInviteLabel}
                        </p>
                    }
                  </div>
                  <div className="col-span-2">
                    {
                      user.email
                        ? (
                          <a
                            href={`mailto:${user.email}`}
                            className="text-green-800 underline text-sm leading-[19.6px]"
                          >
                            {user.email}
                          </a>)
                        : textSpec.table.emptyLabel
                    }
                  </div>
                  <div className="col-span-1 flex justify-end items-center">
                    <Dropdown horizontal>
                      {
                        user?.invite_accepted_at
                        && (
                          <Dropdown.Item
                            onClick={() => {
                              setSelectedUser(user)
                              setIsEditModalOpen(true)
                            }}
                          >
                            {textSpec.table.actions.edit}
                          </Dropdown.Item>
                        )
                      }
                      {
                        user?.invite_accepted_at
                        && (
                          <Dropdown.Item
                            onClick={() => {
                              setSelectedUser(user)
                              setIsResetPasswordModalOpen(true)
                            }}
                          >
                            {textSpec.table.actions.resetPassword}
                          </Dropdown.Item>
                        )
                      }
                      {
                        user?.invite_accepted_at
                        && (
                          <Dropdown.Separator />
                        )
                      }
                      <Dropdown.Item
                        onClick={() => {
                          setSelectedUser(user)
                          setIsDeleteModalOpen(true)
                        }}
                      >
                        {textSpec.table.actions.delete}
                      </Dropdown.Item>
                    </Dropdown>
                  </div>
                </div>
              ))
            }
            {
              response && response.data.length === 0
                ? <div>
                  <div className="flex items-center justify-center flex-col gap-3">
                    <UserIcon className="h-8 w-8 p-1 rounded-md" />
                      No users found
                  </div>
                </div>
                : null
            }
            {
              isFetching && (!response || response!.data.length !== 0)
              && <div className="flex items-center justify-center w-full">
                <LoadingSpinner className="h-4 w-4" />
              </div>
            }
          </div>
        </div>
        <div className="px-2 md:px-0">
          <PaginationBar
            currentPage={currentPage}
            lastPage={response?.meta?.last_page ?? 1}
            total={response?.meta?.total ?? 1}
            from={response?.meta?.from}
            to={response?.meta?.to}
            onPageChange={(pageNum) => setPage(pageNum)}
            onRowsPerPageChange={(rowsPerPage) => setItemsPerPage(rowsPerPage)}
          />
        </div>
      </div>
      <ResetUserPasswordModal
        isOpen={isResetPasswordModalOpen}
        onClose={() => setIsResetPasswordModalOpen(false)}
        user={selectedUser}
      />
      <EditTeamMemberModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        user={selectedUser}
      />
      <DeleteTeamMemberModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        user={selectedUser}
      />
    </>
  )
}

export default TeamMemberList
